import { normaliseCableType } from "./normaliseCableType";
import type { CableTypeSummaryEntry, SummariseCableTypes } from "./types";

interface Bucket {
  metres: number;
  circuitIds: string[];
  spellings: Map<string, number>;
  boards: Map<string | null, number>;
}

const mostUsed = (counts: Map<string, number>): string =>
  [...counts.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  )[0]?.[0] ?? "";

export const summariseCableTypes: SummariseCableTypes = ({
  items,
  cableTypeByCircuit = {},
  lengthsByCircuit = {},
  feedingBoardByCircuit = {},
  extraTypes = [],
  splitByCircuit = {},
}) => {
  const buckets = new Map<string, Bucket>();

  const add = (circuitId: string, rawType: string, metres: number) => {
    const cableType = rawType.trim();
    const key = cableType ? normaliseCableType(cableType) : "";
    const board = feedingBoardByCircuit[circuitId] ?? null;
    const bucket: Bucket = buckets.get(key) ?? {
      metres: 0,
      circuitIds: [],
      spellings: new Map(),
      boards: new Map(),
    };
    bucket.metres += metres;
    bucket.circuitIds.push(circuitId);
    if (cableType)
      bucket.spellings.set(
        cableType,
        (bucket.spellings.get(cableType) ?? 0) + 1,
      );
    bucket.boards.set(board, (bucket.boards.get(board) ?? 0) + metres);
    buckets.set(key, bucket);
  };

  items.forEach((item) => {
    if (!item.circuit) return;
    const split = splitByCircuit[item.circuit];
    if (split && split.length > 1) {
      split.forEach((share) => add(item.circuit as string, share.cableType, share.metres));
      return;
    }
    add(
      item.circuit,
      cableTypeByCircuit[item.circuit] ?? item.cable_type ?? "",
      lengthsByCircuit[item.circuit] ?? item.quantity ?? 0,
    );
  });

  extraTypes.forEach((cableType) => {
    const key = normaliseCableType(cableType);
    if (!key || buckets.has(key)) return;
    buckets.set(key, {
      metres: 0,
      circuitIds: [],
      spellings: new Map([[cableType, 1]]),
      boards: new Map(),
    });
  });

  return (
    [...buckets.entries()]
      .map(([key, bucket]): CableTypeSummaryEntry => ({
        key,
        label: mostUsed(bucket.spellings),
        metres: bucket.metres,
        circuitIds: bucket.circuitIds,
        byBoard: [...bucket.boards.entries()]
          .map(([board, metres]) => ({ board, metres }))
          .sort((a, b) => b.metres - a.metres),
      }))
      // Runs with no cable type are the ones to fix, so they close the list rather than hide in it.
      .sort((a, b) => {
        if (!a.key !== !b.key) return a.key ? -1 : 1;
        return b.metres - a.metres;
      })
  );
};
